import React from "react";
import clsx from "clsx";
import TextAutoResize from "./TextAutoResize";
import { WindowData } from "../../RegularClassroomUser/utils";
import { isPlatformChatSupported } from "@/js/utils/chatUtils";
import { ChakraFAIcon } from "@/components/ui/Icon";

interface Props {
	classActive: boolean;
	handleOnChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	inputMessage: string;
	handleMessageSend: () => void;
	chatError: string | null;
	handleChatEnable: () => void;
	isChatTurnedOff?: boolean;
	/** Window data for the student's device. Null if there is no window data coming in (i.e. the student is not online) */
	studentDeviceWindowData: WindowData | null;
}

const ChatInput = ({
	classActive,
	isChatTurnedOff = false,
	handleOnChange,
	handleKeyDown,
	inputMessage,
	handleMessageSend,
	chatError,
	handleChatEnable,
	studentDeviceWindowData,
}: Props): JSX.Element => {
	if (chatError) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>{chatError}</p>
			</div>
		);
	}

	if (!classActive) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>Chat is off until your class is started again.</p>
				<a href="https://docs.classwize.net/help/intro-classtools" target="_blank" rel="noopener noreferrer">
					Learn more
				</a>
			</div>
		);
	}

	if (isChatTurnedOff) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>Chat is currently turned off for this session.</p>
				<button className="ClassroomChatMessagePane_enableChat" onClick={handleChatEnable}>
					Turn on chat
				</button>
			</div>
		);
	}

	if (!studentDeviceWindowData) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>Chat is currently unavailable, the student is not yet online.</p>
			</div>
		);
	}

	if (!isPlatformChatSupported(studentDeviceWindowData)) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>Chat is currently unavailable, the student's device does not support chat.</p>
			</div>
		);
	}

	return (
		<div className="ClassroomChatMessagePane_controlsPane">
			<div
				className={clsx("ClassroomChatMessagePane_textInputWrapper", {
					"ClassroomChatMessagePane_textInput--hasContent": inputMessage.length,
				})}
			>
				<TextAutoResize
					autoComplete="off"
					className="ClassroomChatMessagePane_textInput"
					onChange={handleOnChange}
					placeholder="Type a message"
					value={inputMessage}
					disabled={isChatTurnedOff}
					onKeyDown={handleKeyDown}
				/>
			</div>
			<button
				className="ClassroomChatMessagePane_sendButton"
				disabled={isChatTurnedOff || !inputMessage.length || inputMessage.length >= 1000}
				onClick={handleMessageSend}
				type="button"
			>
				<ChakraFAIcon icon="fa-paper-plane" variant="solid" color="inherit" boxSize={18} mx={2} />
			</button>
		</div>
	);
};

export default ChatInput;
